/**
 * Contract Date Calculation Hook
 * Provides automatic contract date calculation based on child age and kindergarten enrollment
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import type {
  ContractDateCalculationRequest,
  ContractDateCalculationResponse,
  ContractDateCalculationVariables,
} from '@/types/contract-dates';
import {
  isCalculationError,
  validateCalculationResponse,
} from '@/types/contract-dates';
import { ContractsService } from '@/api/services/ContractsService';
import { contractDateKeys } from './contract-date-keys';

/**
 * Converts date input to FlexibleDate format required by backend
 * Handles both ISO date strings and FlexibleDate objects
 * @throws {TypeError} If input is null or undefined
 * @throws {Error} If date string is invalid or cannot be parsed
 */
export function toFlexibleDate(input: string | { time: string } | null | undefined): { time: string } {
  // Guard against null/undefined (feedback point b)
  if (input === null || input === undefined) {
    throw new TypeError('toFlexibleDate: input is null or undefined');
  }

  if (typeof input === 'string') {
    // YYYY-MM-DD format -> convert to RFC3339 with midnight UTC
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
      // Verify date is actually valid (catches Feb 30, Month 13, etc.)
      const date = new Date(`${input}T00:00:00Z`);
      if (isNaN(date.getTime())) {
        throw new Error(`Invalid date: ${input}`);
      }
      return { time: `${input}T00:00:00Z` };
    }
    // Other formats -> parse and convert to ISO
    const date = new Date(input);
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date format: ${input}`);
    }
    return { time: date.toISOString() };
  }
  return input;
}

/**
 * Hook to calculate contract dates based on child information
 *
 * @returns Mutation object with methods to trigger calculation
 *
 * @example
 * const { mutate, isPending } = useContractDateCalculation();
 *
 * // Trigger calculation with required fields
 * mutate(
 *   {
 *     childBirthdate: '2020-05-15',
 *     childcareId: 'childcare-uuid',
 *     organizationId: 'org-uuid',
 *   },
 *   {
 *     onSuccess: (data) => {
 *       // Use calculated dates to update form
 *       form.setValue('startDate', data.startDate);
 *       form.setValue('endDate', data.endDate);
 *     },
 *     onError: (error) => {
 *       console.error('Calculation failed:', error);
 *     },
 *   }
 * );
 */
export function useContractDateCalculation(options?: {
  /** No success toast: the caller shows the calculated dates in its own toast. */
  silent?: boolean;
}) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const { orgId } = useAuthContext();
  const silent = options?.silent ?? false;

  return useMutation({
    mutationFn: async (params: ContractDateCalculationVariables) => {
      const requestData: ContractDateCalculationRequest = {
        child_birthdate: toFlexibleDate(params.childBirthdate),
        childcare_id: params.childcareId,
        organization_id: params.organizationId || orgId || undefined,
      };

      // Backend needs childBirthdate, childcareId, and organizationId
      const response = await ContractsService.calculateContractDates(requestData);

      // Type guard to ensure proper response type
      if (isCalculationError(response)) {
        throw new Error(response.error);
      }

      const parsed = validateCalculationResponse(response);
      if (!parsed.success) {
        throw new Error(
          `Invalid response format from server: ${JSON.stringify(parsed.error.format())}`
        );
      }

      return parsed.data;
    },

    onSuccess: (data, variables: ContractDateCalculationVariables) => {
      // Cache the calculation result using childcareId as cache key
      if (variables.childcareId) {
        queryClient.setQueryData<ContractDateCalculationResponse>(
          contractDateKeys.calculation(variables.childcareId),
          data
        );
      }

      if (silent) return;

      // Show success message based on whether kindergarten date was used
      const message = data.metadata?.kindergartenStartDate
        ? i18n._('contracts.toast.datesCalculatedKindergarten')
        : i18n._('contracts.toast.datesCalculated');

      toast.success(message);
    },

    onError: (error: Error) => {
      const errorMessage = error.message || 'Unknown error';
      const lowerMessage = errorMessage.toLowerCase();

      // Handle specific error cases
      if (lowerMessage.includes('not found') || lowerMessage.includes('child_not_found')) {
        toast.error(i18n._('contracts.error.childNotFound'));
        return;
      }

      if (lowerMessage.includes('permission') || lowerMessage.includes('forbidden')) {
        toast.error(i18n._('common.error.permissionDenied'));
        return;
      }

      // Check for childcare/canton configuration errors
      if (lowerMessage.includes('canton') || lowerMessage.includes('childcare')) {
        toast.error(i18n._('contracts.error.missingChildcareConfig'));
        return;
      }

      if (lowerMessage.includes('kindergarten') && lowerMessage.includes('date')) {
        toast.error(i18n._('contracts.error.missingKindergartenDate'));
        return;
      }

      // Check for birthdate errors - use precise matching to avoid false positives
      // from validation errors that contain "child_birthdate" as a field name
      if (
        lowerMessage.includes('birthdate is required') ||
        lowerMessage.includes('birthdate is missing') ||
        (lowerMessage.includes('birthdate') && !lowerMessage.includes('child_birthdate'))
      ) {
        toast.error(i18n._('contracts.error.missingBirthdate'));
        return;
      }

      // Check for validation errors with childcare field context
      if (lowerMessage.includes('validation') && lowerMessage.includes('childcare_id')) {
        toast.error(i18n._('contracts.error.missingChildcareConfig'));
        return;
      }

      if (lowerMessage.includes('validation')) {
        toast.error(i18n._('common.error.validationFailed'));
        return;
      }

      // Generic error fallback
      toast.error(
        i18n._('contracts.error.calculationFailed'),
        {
          description: errorMessage,
        }
      );
    },
  });
}
